import type { ReactNode } from "react";
import { Icons } from "../components/Icons.js";
import { CaptureView } from "../capture/CaptureView.js";
import { NotesView } from "../notes/NotesView.js";
import { ResumesView } from "../notes/ResumesView.js";
import type { EditTarget } from "../notes/types.js";
import type { Recording } from "../core/useRecording.js";
import type { Appearance } from "../theme/useAppearance.js";
import type { UsageData } from "../lib/api.js";
import type { Screen } from "./nav.js";

function NavBtn({
  icon,
  label,
  active,
  badge,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  badge?: number | null;
  onClick: () => void;
}) {
  return (
    <button className={"dnav-b" + (active ? " on" : "")} onClick={onClick}>
      <span className="dnav-ic">{icon}</span>
      <span className="dnav-l">{label}</span>
      {badge ? <span className="dnav-badge">{badge}</span> : null}
    </button>
  );
}

function DesktopHome(props: {
  recording: Recording;
  btnScale: number;
  simple: boolean;
  usageRatio: number | null;
  bgDaily: string;
  bgStandalone: string;
  onWrite: () => void;
  onResumes: () => void;
  goNotes: () => void;
  notesCount: number;
}) {
  const { recording, btnScale, simple, usageRatio, bgDaily, bgStandalone, onWrite, onResumes, goNotes, notesCount } = props;
  return (
    <div className="dhome">
      <div className="dhome-hero">
        <CaptureView recording={recording} btnScale={btnScale} desktop simple={simple} usageRatio={usageRatio} bgDaily={bgDaily} bgStandalone={bgStandalone} />
      </div>
      <div className="dhome-cards">
        <button className="dcard" onClick={onWrite}>
          <span className="dcard-ic"><Icons.Pen size={24} /></span>
          <span className="dcard-l">Note écrite / photos</span>
          <span className="dcard-s">Rédiger au clavier, ajouter des photos</span>
        </button>
        <button className="dcard" onClick={goNotes}>
          <span className="dcard-ic"><Icons.List size={24} /></span>
          <span className="dcard-l">Mes notes</span>
          <span className="dcard-s">{notesCount ? `${notesCount} enregistrée${notesCount > 1 ? "s" : ""}` : "Aucune pour l'instant"}</span>
        </button>
        <button className="dcard" onClick={onResumes}>
          <span className="dcard-ic"><Icons.Doc size={24} /></span>
          <span className="dcard-l">Résumés</span>
          <span className="dcard-s">Comptes rendus jour / semaine</span>
        </button>
      </div>
    </div>
  );
}

export interface DesktopShellProps {
  screen: Screen;
  setScreen: (s: Screen) => void;
  recording: Recording;
  btnScale: number;
  simple: boolean;
  usageRatio: number | null;
  usage: UsageData | null;
  onWrite: () => void;
  onOpen: (t: EditTarget) => void;
  reloadToken: number;
  notesCount: number;
  justSaved: boolean;
  appearance: Appearance;
  onChanged: () => void;
  onLogout: () => void;
  /** Les paramètres vivent dans une fenêtre superposée tenue par App ; la barre latérale l'ouvre. */
  settingsOpen: boolean;
  onOpenSettings: () => void;
}

/** Disposition bureau : barre latérale de navigation + panneau principal (porté du mockup). */
export function DesktopShell(p: DesktopShellProps) {
  let main: ReactNode = null;
  if (p.screen === "home") {
    main = (
      <DesktopHome
        recording={p.recording}
        btnScale={p.btnScale}
        simple={p.simple}
        usageRatio={p.usageRatio}
        bgDaily={p.appearance.bgDaily}
        bgStandalone={p.appearance.bgStandalone}
        onWrite={p.onWrite}
        onResumes={() => p.setScreen("resumes")}
        goNotes={() => p.setScreen("notes")}
        notesCount={p.notesCount}
      />
    );
  } else if (p.screen === "notes") {
    main = (
      <div className="dsection">
        <div className="dsection-head"><h1>Mes notes</h1></div>
        <NotesView onOpen={p.onOpen} reloadToken={p.reloadToken} />
      </div>
    );
  } else if (p.screen === "resumes") {
    main = (
      <div className="dsection">
        <div className="dsection-head"><h1>Comptes rendus</h1></div>
        <ResumesView onOpen={p.onOpen} reloadToken={p.reloadToken} />
      </div>
    );
  }

  return (
    <div className="deskapp">
      <aside className="dsidebar">
        <div className="dbrand">
          <span className="dbrand-mark"><Icons.Mic size={20} /></span> Pensieve
        </div>
        <nav className="dnav">
          <NavBtn icon={<Icons.Mic size={20} />} label="Capturer" active={p.screen === "home"} onClick={() => p.setScreen("home")} />
          <NavBtn icon={<Icons.List size={20} />} label="Mes notes" badge={p.notesCount || null} active={p.screen === "notes"} onClick={() => p.setScreen("notes")} />
          <NavBtn icon={<Icons.Doc size={20} />} label="Comptes rendus" active={p.screen === "resumes"} onClick={() => p.setScreen("resumes")} />
        </nav>
        <div className="dsidebar-foot">
          {/* Les paramètres s'ouvrent en fenêtre superposée (gérée par App), pas en écran : même
              présentation et même largeur que les fenêtres de note et de résumé. */}
          <NavBtn icon={<Icons.Gear size={20} />} label="Paramètres" active={p.settingsOpen} onClick={p.onOpenSettings} />
        </div>
      </aside>
      <main className="dmain">
        {p.appearance.bgImage ? <div className="bg-layer" style={{ backgroundImage: p.appearance.bgImage }} /> : null}
        <div className="dmain-inner">{main}</div>
        {p.justSaved && <div className="savedtoast desk">Enregistré</div>}
      </main>
    </div>
  );
}
